import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddressBook from "@/pages/AddressBook";
import FoundingBadge from "@/components/FoundingBadge";
import { MyRecipesPanel } from "@/pages/Recipes";
import BirthdayRewardCard from "@/components/BirthdayRewardCard";
import MembershipSettings from "@/components/MembershipSettings";
import PauseExtensionNudge from "@/components/PauseExtensionNudge";
import MembershipShare from "@/components/MembershipShare";

function VerifyBanner({ user, onResent }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (user?.email_verified) return null;
  const resend = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post("/auth/resend-verification");
      setMsg("Verification email sent — check your inbox.");
      onResent?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not resend.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 card-flat p-5 bg-[var(--ff-citrus)]/10 border-[var(--ff-secondary)]/30" data-testid="verify-banner">
      <p className="overline text-[var(--ff-secondary)]">Email verification required</p>
      <h3 className="font-display text-2xl mt-1">Verify {user.email} to unlock checkout.</h3>
      <p className="text-sm text-[var(--ff-text-secondary)] mt-2">
        Purchases and membership subscription are blocked until your email is confirmed.
        Click the link in your welcome email, or resend it below.
      </p>
      <button onClick={resend} disabled={busy} className="btn-primary mt-4" data-testid="resend-verify-btn">
        {busy ? "Sending…" : "Resend verification email"}
      </button>
      {msg && <p className="mt-2 text-xs text-[var(--ff-text-muted)]" data-testid="resend-verify-msg">{msg}</p>}
    </div>
  );
}

function ManageBillingButton() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const open = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/memberships/portal", { return_url: window.location.origin + "/account" });
      if (data.ok && data.url) { window.location.href = data.url; return; }
      setMsg(data.message || "Billing portal not yet available.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Error opening portal");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-6" data-testid="billing-portal-wrap">
      <button onClick={open} disabled={busy} className="btn-secondary" data-testid="manage-billing-btn">
        {busy ? "Loading…" : "Manage billing"}
      </button>
      {msg && <p className="mt-2 text-xs text-[var(--ff-text-muted)] max-w-md" data-testid="billing-portal-msg">{msg}</p>}
    </div>
  );
}

function SnoozeWidget({ membership, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [weeks, setWeeks] = useState(2);
  const [err, setErr] = useState("");
  const snoozed = membership?.snoozed_until && new Date(membership.snoozed_until) > new Date();

  const snooze = async () => {
    setBusy(true); setErr("");
    try { await api.post("/memberships/snooze", { weeks }); onChanged?.(); }
    catch (e) { setErr(e.response?.data?.detail || "Snooze failed"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true); setErr("");
    try { await api.post("/memberships/resume"); onChanged?.(); }
    catch (e) { setErr(e.response?.data?.detail || "Resume failed"); }
    finally { setBusy(false); }
  };

  if (snoozed) {
    return (
      <div className="mt-6 card-flat p-5 bg-[var(--ff-muted)]" data-testid="snooze-active-panel">
        <p className="overline">Snoozed</p>
        <p className="mt-2 text-sm">Deliveries paused until <strong>{new Date(membership.snoozed_until).toLocaleDateString()}</strong>.</p>
        <button onClick={resume} disabled={busy} className="btn-primary mt-4" data-testid="resume-btn">
          {busy ? "Resuming…" : "Resume now"}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-6 card-flat p-5" data-testid="snooze-panel">
      <p className="overline">Skip a week or two</p>
      <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Going on vacation? Pause your deliveries and resume anytime.</p>
      <div className="mt-4 flex gap-3 items-center">
        <select className="input-base max-w-[8rem]" value={weeks} onChange={e => setWeeks(parseInt(e.target.value))} data-testid="snooze-weeks-select">
          <option value={1}>1 week</option>
          <option value={2}>2 weeks</option>
          <option value={4}>4 weeks</option>
          <option value={8}>8 weeks</option>
        </select>
        <button onClick={snooze} disabled={busy} className="btn-secondary" data-testid="snooze-btn">
          {busy ? "Snoozing…" : `Skip ${weeks} ${weeks===1?"week":"weeks"}`}
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-red-700">{err}</p>}
    </div>
  );
}

function UpgradePrompt({ membership, onUpgraded }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (membership?.plan_id !== "plan_monthly") return null;

  const upgrade = async () => {
    if (!window.confirm("Upgrade to annual? Your card will be prorated for the new plan.")) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/memberships/upgrade", { target_plan_id: "plan_yearly" });
      setMsg(`✓ Upgraded to ${data.new_plan}.`);
      onUpgraded?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Upgrade failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 card-flat p-5 bg-[var(--ff-citrus)]/10 border-[var(--ff-secondary)]/30" data-testid="upgrade-prompt">
      <p className="overline text-[var(--ff-secondary)]">Save $49 / year</p>
      <h3 className="font-display text-2xl mt-1">Switch to annual and lock in 2 months free.</h3>
      <p className="text-sm text-[var(--ff-text-secondary)] mt-2">Same membership, billed yearly. Stripe will prorate today.</p>
      <button onClick={upgrade} disabled={busy} className="btn-primary mt-4" data-testid="upgrade-btn">
        {busy ? "Upgrading…" : "Upgrade to annual"}
      </button>
      {msg && <p className="mt-2 text-xs">{msg}</p>}
    </div>
  );
}

function StripePromoReferralCard() {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get("/account/referral"),
          api.get("/account/referral/stats"),
        ]);
        if (alive) {
          setInfo(r1.data);
          setStats(r2.data);
        }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Could not load referral.");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) return <p className="text-xs text-rose-700" data-testid="stripe-referral-err">{err}</p>;
  if (!info) return null;

  const shareUrl = `${window.location.origin}/ref/${info.code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  if (info.is_ambassador) {
    return (
      <div className="card-flat p-6 text-white" style={{ background: "var(--ff-primary)" }} data-testid="stripe-referral-ambassador">
        <p className="overline text-white/70">Refer a friend with $10 off</p>
        <p className="mt-2 text-sm text-white/90">
          You're an Ambassador — you have a full commission dashboard. Manage everything there.
        </p>
        <Link to="/ambassador/dashboard" className="mt-4 inline-flex btn-secondary !bg-white !text-[var(--ff-primary)]" data-testid="ambassador-dashboard-link">
          Open Ambassador dashboard →
        </Link>
      </div>
    );
  }

  return (
    <div className="card-flat p-6" data-testid="stripe-referral-card">
      <p className="overline text-[var(--ff-secondary)]">Refer a friend · $10 off their first order</p>
      <h3 className="font-display text-2xl mt-2 text-[var(--ff-text)]">Share the table, save them $10.</h3>
      <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">
        Send your friends a personal link. $10 is taken off their first order automatically at checkout — no codes to remember.
      </p>

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <input
          readOnly
          value={shareUrl}
          className="input-base flex-1 text-sm font-mono"
          data-testid="stripe-referral-url"
        />
        <button onClick={copy} className="btn-primary inline-flex items-center gap-2" data-testid="stripe-referral-copy">
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[var(--ff-text-muted)]">
        <span>Code: <strong className="font-mono">{info.code}</strong></span>
        {info.stripe_promo_minted
          ? <span className="text-emerald-700">· $10 off active</span>
          : <span className="text-amber-700">· promo activating within 1h</span>}
      </div>

      {stats && (stats.signups > 0 || stats.clicks > 0) && (
        <div className="mt-5 grid grid-cols-3 gap-3 text-center border-t border-[var(--ff-border)] pt-4">
          <div data-testid="stripe-referral-clicks">
            <p className="text-2xl font-display text-[var(--ff-primary)]">{stats.clicks}</p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--ff-text-muted)] mt-1">Clicks</p>
          </div>
          <div data-testid="stripe-referral-signups">
            <p className="text-2xl font-display text-[var(--ff-primary)]">{stats.signups}</p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--ff-text-muted)] mt-1">Friends joined</p>
          </div>
          <div data-testid="stripe-referral-savings">
            <p className="text-2xl font-display text-[var(--ff-primary)]">${stats.savings_usd.toFixed(0)}</p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--ff-text-muted)] mt-1">Saved for friends</p>
          </div>
        </div>
      )}
    </div>
  );
}


function ReferralPanel() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referrals/me").then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <p className="text-[var(--ff-text-muted)]" data-testid="referrals-loading">Loading…</p>;

  const inviteUrl = `${window.location.origin}${data.invite_url_suffix}`;
  const copy = () => {
    navigator.clipboard?.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5" data-testid="referral-panel">
      <StripePromoReferralCard />

      <div className="card-flat p-6">
        <p className="overline">Your referral code</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code className="font-mono text-2xl bg-[var(--ff-muted)] px-4 py-2 rounded-lg" data-testid="referral-code">{data.code}</code>
          <span className="text-sm text-[var(--ff-text-muted)]">share with friends</span>
        </div>
        <div className="mt-5">
          <p className="text-xs text-[var(--ff-text-muted)] uppercase tracking-widest">Share link</p>
          <div className="mt-2 flex gap-2">
            <input readOnly value={inviteUrl} className="input-base flex-1 text-sm" data-testid="referral-url"/>
            <button onClick={copy} className="btn-secondary" data-testid="referral-copy-btn">
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-flat p-5" data-testid="referral-stat-total">
          <p className="overline">Friends invited</p>
          <p className="font-display text-4xl mt-2">{data.referees_total}</p>
        </div>
        <div className="card-flat p-5" data-testid="referral-stat-converted">
          <p className="overline">Memberships</p>
          <p className="font-display text-4xl mt-2">{data.referees_converted}</p>
        </div>
        <div className="card-flat p-5" data-testid="referral-stat-credits">
          <p className="overline">Credits earned</p>
          <p className="font-display text-4xl mt-2">${data.credits_usd.toFixed(2)}</p>
        </div>
      </div>

      <p className="text-xs text-[var(--ff-text-muted)] max-w-lg">
        Both you and your friend earn <strong>${data.reward_per_conversion} credit</strong> the moment they activate a paid membership.
        Credits apply automatically to your next renewal.
      </p>

      <a href="/referrals/leaderboard" className="inline-flex items-center gap-1 text-sm text-[var(--ff-primary)] underline" data-testid="referral-leaderboard-link">
        See the top-referrers leaderboard →
      </a>
    </div>
  );
}

function SmsPrefsCard({ user }) {
  const { refresh } = useAuth();
  const [optIn, setOptIn] = useState(!!user?.sms_opt_in);
  const [marketingOptIn, setMarketingOptIn] = useState(!!user?.sms_marketing_opt_in);
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!user) return;
    setOptIn(!!user.sms_opt_in);
    setMarketingOptIn(!!user.sms_marketing_opt_in);
    if (user.phone) setPhone(user.phone);
  }, [user]);
  const save = async () => {
    setBusy(true); setMsg("");
    const hasPhone = !!phone.trim();
    try {
      await api.patch("/auth/sms-prefs", {
        sms_opt_in: hasPhone ? optIn : false,
        sms_marketing_opt_in: hasPhone ? marketingOptIn : false,
        phone,
        source: "account_sms_prefs",
      });
      await refresh();
      setMsg("Saved.");
    } catch (e) { setMsg("Couldn't save — try again."); }
    setBusy(false);
  };
  return (
    <div className="card-flat p-6 mt-6" data-testid="sms-prefs-card">
      <p className="overline">SMS preferences</p>
      <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">Choose which texts you'd like to receive.</p>
      <input className="input-base mt-4 max-w-xs" placeholder="+1 (555) 555-5555" type="tel" value={phone} onChange={e => { const v = e.target.value; setPhone(v); if (!v.trim()) { setOptIn(false); setMarketingOptIn(false); } }} data-testid="sms-phone-input"/>
      <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
        <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]" checked={optIn} disabled={!phone} onChange={e => setOptIn(e.target.checked)} data-testid="sms-opt-in-toggle"/>
        <span className="text-xs leading-relaxed text-[var(--ff-text-secondary)]">
          I agree to receive order &amp; account texts (delivery updates, receipts) from The Farm To You at the number above. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.
        </span>
      </label>
      <label className="mt-3 flex items-start gap-3 cursor-pointer select-none">
        <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]" checked={marketingOptIn} disabled={!phone} onChange={e => setMarketingOptIn(e.target.checked)} data-testid="sms-marketing-opt-in-toggle"/>
        <span className="text-xs leading-relaxed text-[var(--ff-text-secondary)]">
          I agree to receive marketing &amp; promotional texts (deals, new drops) from The Farm To You at the number above. Consent is not a condition of purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel. See our{" "}
          <Link to="/terms#sms" target="_blank" className="underline text-[var(--ff-primary)]">Terms</Link> &amp;{" "}
          <Link to="/privacy#sms" target="_blank" className="underline text-[var(--ff-primary)]">Privacy Policy</Link>.
        </span>
      </label>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="sms-prefs-save">{busy ? "Saving…" : "Save preferences"}</button>
        {msg && <span className="text-sm text-[var(--ff-text-secondary)]" data-testid="sms-prefs-msg">{msg}</span>}
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [membership, setMembership] = useState(null);
  const [favs, setFavs] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then(r => setOrders(r.data)).catch(()=>{});
    api.get("/memberships/mine").then(r => setMembership(r.data)).catch(()=>{});
    api.get("/favorites").then(r => setFavs(r.data)).catch(()=>{});
  }, [user]);

  if (!user) return <div className="p-10" data-testid="dash-no-user">Please sign in.</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="customer-dashboard">
      <PauseExtensionNudge />
      <p className="overline">Your account</p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <h1 className="font-display text-5xl">Welcome, {user.name.split(" ")[0]}.</h1>
        {user.is_founding_member && <FoundingBadge size="md" data-testid="dashboard-founding-badge"/>}
      </div>
      {user.is_founding_member && (
        <p className="mt-3 text-sm text-[var(--ff-text-secondary)]" data-testid="dashboard-founding-perks">
          Founding Member ·
          {user.free_shipping_credits > 0
            ? <> <strong className="text-[var(--ff-primary)]">{user.free_shipping_credits} free-shipping credit</strong> ready for your first drop order.</>
            : <> thanks for backing us pre-launch.</>}
        </p>
      )}

      <VerifyBanner user={user} onResent={refresh}/>

      <div className="mt-8 flex gap-2 border-b border-[var(--ff-border)] overflow-x-auto">
        {["overview","orders","recipes","membership","addresses","saved","referrals"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm capitalize whitespace-nowrap ${tab===t ? 'border-b-2 border-[var(--ff-primary)] text-[var(--ff-primary)] font-semibold' : 'text-[var(--ff-text-secondary)]'}`}
            data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "overview" && (
          <div className="space-y-8">
            <BirthdayRewardCard />
            <div className="grid md:grid-cols-3 gap-6">
              <div className="card-flat p-6" data-testid="stat-orders">
                <p className="overline">Total orders</p>
                <p className="font-display text-5xl mt-3">{orders.filter(o => o.status !== 'pending').length}</p>
              </div>
              <div className="card-flat p-6" data-testid="stat-membership">
                <p className="overline">Membership</p>
                <p className="font-display text-3xl mt-3 capitalize">{membership?.status || "None"}</p>
                {membership?.plan_name && <p className="text-sm text-[var(--ff-text-muted)] mt-1">{membership.plan_name}</p>}
              </div>
              <div className="card-flat p-6" data-testid="stat-favorites">
                <p className="overline">Saved items</p>
                <p className="font-display text-5xl mt-3">{favs.length}</p>
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Your orders</p>
              <Link to="/account/orders" className="text-sm text-[var(--ff-primary)] underline" data-testid="orders-track-all">
                Track all orders →
              </Link>
            </div>
            <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="orders-list">
              {orders.filter(o => o.status !== 'pending').length === 0 && <p className="p-8 text-[var(--ff-text-muted)]">No orders yet.</p>}
              {orders.filter(o => o.status !== 'pending').map(o => (
                <Link key={o.id} to={`/account/orders/${o.id}`} className="p-5 flex justify-between items-center hover:bg-[var(--ff-muted)] transition-colors" data-testid={`order-${o.id}`}>
                  <div>
                    <p className="font-medium">Order #{o.id.slice(0,8)}</p>
                    <p className="text-xs text-[var(--ff-text-muted)] mt-1">{o.items.length} items · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${o.total.toFixed(2)}</p>
                    <p className="text-xs uppercase tracking-wider mt-1 text-[var(--ff-primary)]">{o.fulfillment_status || o.status}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {tab === "membership" && (
          <div className="space-y-6">
            <MembershipSettings
              membership={membership}
              onChanged={() => api.get("/memberships/mine").then(r => setMembership(r.data))}
            />
            <MembershipShare />
          </div>
        )}

        {tab === "referrals" && <ReferralPanel/>}
        {tab === "recipes" && <MyRecipesPanel/>}

        {tab === "addresses" && <><AddressBook embedded /><SmsPrefsCard user={user} /></>}

        {tab === "saved" && <><SavedTab favs={favs}/><FollowingRanchers/></>}
      </div>
    </div>
  );
}

function SavedTab({ favs }) {
  const [drops, setDrops] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/drops/me/favorites").then(r => setDrops(r.data.drops)).catch(() => setDrops([]));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    setBusy(true);
    try { await api.post(`/drops/${slug}/favorite`); await load(); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="saved-tab">
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="overline">Saved drops</p>
            <h2 className="font-display text-3xl mt-1">Drops you're watching</h2>
          </div>
          <Link to="/drops" className="text-sm text-[var(--ff-primary)] underline" data-testid="saved-drops-browse">Browse drops →</Link>
        </div>
        {!drops && <p className="text-[var(--ff-text-muted)]">Loading…</p>}
        {drops && drops.length === 0 && (
          <div className="card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="saved-drops-empty">
            <p>You haven't saved any drops yet.</p>
            <p className="text-xs mt-2">Tap the heart on any drop to keep an eye on it — we'll notify you the moment it goes live.</p>
            <Link to="/drops" className="btn-primary inline-flex mt-4">See current drops</Link>
          </div>
        )}
        {drops && drops.length > 0 && (
          <div className="grid md:grid-cols-3 gap-5" data-testid="saved-drops-grid">
            {drops.map(d => (
              <div key={d.slug} className="card-flat overflow-hidden" data-testid={`saved-drop-${d.slug}`}>
                <Link to={`/drops/${d.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-[var(--ff-muted)]">
                    {d.image_url && <img src={d.image_url} className="w-full h-full object-cover" alt={d.name}/>}
                  </div>
                </Link>
                <div className="p-4">
                  <p className="overline">{d.farm_name}</p>
                  <Link to={`/drops/${d.slug}`} className="block">
                    <h3 className="font-display text-xl mt-1 hover:text-[var(--ff-primary)]">{d.name}</h3>
                  </Link>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-semibold">${d.price.toFixed(0)}</span>
                    <button disabled={busy} onClick={() => remove(d.slug)} className="text-xs text-rose-700 hover:underline" data-testid={`unsave-${d.slug}`}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {favs?.length > 0 && (
        <div>
          <p className="overline">Saved products (legacy)</p>
          <div className="mt-4 grid md:grid-cols-4 gap-5" data-testid="favorites-grid">
            {favs.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="card-flat overflow-hidden product-card">
                <div className="aspect-square"><img src={p.image_url} className="w-full h-full object-cover" alt={p.name}/></div>
                <div className="p-4"><p className="text-sm">{p.name}</p><p className="text-[var(--ff-primary)] font-semibold">${p.price.toFixed(2)}</p></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function FollowingRanchers() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/marketplace/me/following").then(r => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="mt-10" data-testid="following-ranchers">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl">Ranchers you follow</h3>
        <Link to="/ranchers" className="text-sm text-[var(--ff-primary)] underline">Discover ranchers →</Link>
      </div>
      {rows.length === 0 ? (
        <div className="card-flat p-8 text-center text-[var(--ff-text-muted)] mt-4" data-testid="following-empty">Follow a ranch to keep tabs on their new products and drops.</div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(v => (
            <Link key={v.id} to={`/ranchers/${v.id}`} className="card-flat p-4 flex items-center gap-3 hover:border-[var(--ff-primary)]" data-testid={`following-${v.id}`}>
              <img src={v.logo_url || v.image_url} alt="" className="w-12 h-12 rounded-full object-cover"/>
              <div><p className="font-medium">{v.farm_name}</p><p className="text-xs text-[var(--ff-text-muted)]">{v.location}</p></div>
            </Link>))}
        </div>
      )}
    </div>
  );
}
